import { useMutation } from '@tanstack/react-query';
import type { ProblemSlug } from '@/api/generated-types';
import { toastApiError } from '@/components/Notifications/apiErrorToast';
import { seedTransactionFromExport, triggerServerError } from './api';

/**
 * Fires a real server error for the given slug and surfaces it exactly as the
 * app would in production — through {@link toastApiError}, so the admin sees the
 * genuine toast (incl. the copy-trace-id action on 5xx) with a real backend trace id.
 */
export function useTriggerServerError() {
  return useMutation({
    mutationFn: (slug: ProblemSlug) => triggerServerError(slug),
    onError: (err) => toastApiError(err, 'Dev-triggered error'),
  });
}

/**
 * Rebuilds a debug export as a transaction here.
 *
 * The gate answers a bodiless 404 where seeding is off, so `getErrorMessage` falls through to the
 * default — which therefore has to name the configuration state rather than read as a missing
 * transaction. A rejected artifact does carry a message, and that one wins.
 */
export function useSeedFromExport() {
  return useMutation({
    mutationFn: (input: { exportFile: File; sourcePdf: File | null }) =>
      seedTransactionFromExport(input.exportFile, input.sourcePdf),
    onError: (err) =>
      toastApiError(err, 'Seeding is only enabled on local and dev (Dev:SeedingEnabled).'),
  });
}
